import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconButton, Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverBody,
  PopoverArrow, Box, Heading, Text, HStack, VStack, Badge, Avatar, Button,
  useDisclosure, Flex, Tooltip, Divider,
} from "@chakra-ui/react";
import { BellIcon } from "@chakra-ui/icons";
import { api } from "../lib/api";

const TYPE_ICON = {
  task_assigned: "📌", task_unassigned: "🚫", task_comment: "💬", task_mention: "🔔",
  task_completed: "✅", task_overdue: "⏰",
  project_member_added: "📁", project_member_removed: "🚪",
  role_changed: "👤", manager_changed: "🧭",
  invite_accepted: "👋", system: "•",
};

function timeAgo(d) {
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const disc = useDisclosure();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  async function loadCount() {
    try {
      const d = await api("/notifications/unread-count");
      setUnread(d.count || 0);
    } catch (e) {}
  }

  async function loadList() {
    setLoading(true);
    try {
      const d = await api("/notifications?limit=20");
      setItems(d.notifications || []);
      setUnread(d.unreadCount || 0);
    } catch (e) {}
    finally { setLoading(false); }
  }

  useEffect(() => {
    if (!localStorage.getItem("token")) return;
    loadCount();
    pollRef.current = setInterval(loadCount, 30000);
    return () => clearInterval(pollRef.current);
  }, []);

  async function open() {
    disc.onOpen();
    loadList();
  }

  async function markAllRead() {
    try {
      await api("/notifications/read-all", { method: "POST" });
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, readAt: n.readAt || new Date().toISOString() })));
    } catch (e) {}
  }

  async function clickItem(n) {
    if (!n.readAt) {
      try { await api(`/notifications/${n._id}/read`, { method: "POST" }); } catch (e) {}
      setItems((prev) => prev.map((x) => (x._id === n._id ? { ...x, readAt: new Date().toISOString() } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.targetUrl) {
      disc.onClose();
      navigate(n.targetUrl);
    }
  }

  return (
    <Popover isOpen={disc.isOpen} onClose={disc.onClose} placement="bottom-end">
      <PopoverTrigger>
        <Box position="relative">
          <Tooltip label="Notifications">
            <IconButton
              variant="ghost"
              aria-label="notifications"
              icon={<BellIcon />}
              onClick={open}
            />
          </Tooltip>
          {unread > 0 && (
            <Badge
              position="absolute" top="0" right="0"
              colorScheme="red" rounded="full" fontSize="2xs"
              minW="18px" h="18px" lineHeight="18px" textAlign="center"
              border="2px solid white"
            >
              {unread > 99 ? "99+" : unread}
            </Badge>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent w="380px" maxH="540px" overflow="hidden" shadow="lg">
        <PopoverArrow />
        <PopoverHeader borderBottomWidth="1px" pb={2}>
          <Flex justify="space-between" align="center">
            <Heading size="sm">Notifications</Heading>
            {unread > 0 && (
              <Button size="xs" variant="link" colorScheme="blue" onClick={markAllRead}>
                Mark all read
              </Button>
            )}
          </Flex>
        </PopoverHeader>
        <PopoverBody p={0} maxH="460px" overflowY="auto">
          {loading ? (
            <Text fontSize="sm" color="gray.500" p={4} textAlign="center">Loading...</Text>
          ) : items.length === 0 ? (
            <Text fontSize="sm" color="gray.500" p={6} textAlign="center">You're all caught up. ✨</Text>
          ) : (
            <VStack align="stretch" spacing={0} divider={<Divider />}>
              {items.map((n) => (
                <Box
                  key={n._id}
                  px={4} py={3}
                  cursor="pointer"
                  bg={n.readAt ? "white" : "blue.50"}
                  _hover={{ bg: n.readAt ? "gray.50" : "blue.100" }}
                  onClick={() => clickItem(n)}
                >
                  <HStack align="start" spacing={3}>
                    <Box pt={1}>{TYPE_ICON[n.type] || "•"}</Box>
                    <Box flex="1" minW={0}>
                      <Text fontSize="sm" fontWeight={n.readAt ? 400 : 600} noOfLines={2}>
                        {n.title}
                      </Text>
                      {n.body && (
                        <Text fontSize="xs" color="gray.600" noOfLines={2} mt={0.5}>
                          {n.body}
                        </Text>
                      )}
                      <Text fontSize="2xs" color="gray.400" mt={1}>{timeAgo(n.createdAt)}</Text>
                    </Box>
                    {!n.readAt && <Box w={2} h={2} bg="blue.500" rounded="full" mt={2} />}
                  </HStack>
                </Box>
              ))}
            </VStack>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
